import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Search, ShieldAlert, Check, X, Loader2, RefreshCw, User as UserIcon, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { readJsonOnce } from '../utils/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_BADGE = {
  free: 'bg-zinc-100 text-zinc-700',
  solo: 'bg-emerald-100 text-emerald-800',
  pro: 'bg-blue-100 text-blue-800',
  team: 'bg-purple-100 text-purple-800',
  scale: 'bg-amber-100 text-amber-800',
};

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [accessChecked, setAccessChecked] = useState(false);
  const [accessGranted, setAccessGranted] = useState(false);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [plans, setPlans] = useState({});
  const [editingUser, setEditingUser] = useState(null);

  // Verify admin access on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/me`, { credentials: 'include' });
        if (cancelled) return;
        if (r.ok) {
          setAccessGranted(true);
          const plansRes = await fetch(`${API_URL}/api/admin/plans`, { credentials: 'include' });
          const plansData = await readJsonOnce(plansRes);
          if (plansData && typeof plansData === 'object') setPlans(plansData);
        } else {
          setAccessGranted(false);
        }
      } catch (e) {
        console.error('Admin access check failed:', e);
        setAccessGranted(false);
      } finally {
        if (!cancelled) setAccessChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchUsers = useCallback(async () => {
    if (!accessGranted) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (search.trim()) params.set('search', search.trim());
      if (planFilter) params.set('plan', planFilter);
      if (roleFilter) params.set('role', roleFilter);
      const r = await fetch(`${API_URL}/api/admin/users?${params}`, { credentials: 'include' });
      const data = await readJsonOnce(r);
      if (r.ok && data && data.users) {
        setUsers(data.users);
        setTotal(data.total || 0);
      } else {
        toast.error((data && data.detail) || 'Failed to load users');
      }
    } catch (e) {
      console.error(e);
      toast.error('Network error loading users');
    } finally {
      setLoading(false);
    }
  }, [accessGranted, search, planFilter, roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  if (!accessChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!accessGranted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md w-full p-8 text-center" data-testid="admin-no-access">
          <ShieldAlert className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Admin access required</h1>
          <p className="text-sm text-muted-foreground mb-6">
            {user
              ? <>This account (<span className="font-mono">{user.email}</span>) is not in the admin whitelist. Ask the founder to add it to the <code className="bg-secondary px-1 rounded">ADMIN_EMAILS</code> environment variable.</>
              : 'You must be signed in to access the admin panel.'}
          </p>
          <Button onClick={() => navigate('/')} variant="outline" data-testid="admin-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-5 h-5 text-primary" />
            <h1 className="text-xl font-bold">Admin · User Management</h1>
            <Badge variant="outline" className="ml-2 text-xs">{total} users</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="admin-exit-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Filters */}
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-6">
              <label className="text-xs font-medium text-muted-foreground block mb-1">Search by email or name</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="admin-search-input"
                  className="pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="email@domain.com"
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchUsers(); }}
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <label className="text-xs font-medium text-muted-foreground block mb-1">Plan</label>
              <select
                data-testid="admin-plan-filter"
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
              >
                <option value="">All plans</option>
                {Object.values(plans).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-muted-foreground block mb-1">Role</label>
              <select
                data-testid="admin-role-filter"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
              >
                <option value="">All roles</option>
                <option value="sales_rep">Sales Rep</option>
                <option value="manager">Manager</option>
              </select>
            </div>
            <div className="md:col-span-1">
              <Button onClick={fetchUsers} disabled={loading} className="w-full" data-testid="admin-refresh-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </Card>

        {/* User table */}
        <Card className="overflow-hidden" data-testid="admin-user-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 border-b border-border">
                <tr className="text-left">
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Plan</th>
                  <th className="px-4 py-3 font-medium">Cycle</th>
                  <th className="px-4 py-3 font-medium">Verified</th>
                  <th className="px-4 py-3 font-medium">Call limit</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && !loading && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No users match these filters.</td></tr>
                )}
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-border/40 hover:bg-secondary/20" data-testid={`admin-user-row-${u.user_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <UserIcon className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{u.name || '—'}</div>
                          <div className="text-xs text-muted-foreground">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">{u.role === 'manager' ? 'Manager' : 'Sales Rep'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${PLAN_BADGE[u.plan] || 'bg-zinc-100'}`}>
                        {plans[u.plan]?.name || u.plan}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{u.billing_cycle}</td>
                    <td className="px-4 py-3">
                      {u.email_verified ? <Check className="w-4 h-4 text-emerald-600" /> : <X className="w-4 h-4 text-amber-500" />}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {u.call_limit_override !== null && u.call_limit_override !== undefined ? `${u.call_limit_override} (override)` : 'plan default'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingUser(u)}
                        data-testid={`admin-edit-btn-${u.user_id}`}
                      >
                        Manage
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {editingUser && (
        <EditUserModal
          user={editingUser}
          plans={plans}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); fetchUsers(); }}
        />
      )}
    </div>
  );
}

// ---------- Edit modal ----------

function EditUserModal({ user, plans, onClose, onSaved }) {
  const [plan, setPlan] = useState(user.plan || 'free');
  const [cycle, setCycle] = useState(user.billing_cycle || 'monthly');
  const [note, setNote] = useState('');
  const [callLimit, setCallLimit] = useState(
    user.call_limit_override === null || user.call_limit_override === undefined ? '' : String(user.call_limit_override)
  );
  const [verified, setVerified] = useState(Boolean(user.email_verified));
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const deleteUser = async () => {
    setDeleting(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/users/${user.user_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const d = await readJsonOnce(r);
      if (!r.ok) throw new Error((d && d.detail) || 'Failed to delete user');
      toast.success(`User ${user.email} deleted`);
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeleting(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      // 1. plan
      if (plan !== user.plan || cycle !== user.billing_cycle || note.trim()) {
        const r = await fetch(`${API_URL}/api/admin/users/${user.user_id}/plan`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan, billing_cycle: cycle, note: note.trim() || null }),
        });
        const d = await readJsonOnce(r);
        if (!r.ok) throw new Error((d && d.detail) || 'Failed to set plan');
      }

      // 2. call limit
      const newLimit = callLimit.trim() === '' ? null : parseInt(callLimit, 10);
      if (!Number.isNaN(newLimit) && newLimit !== user.call_limit_override) {
        const r = await fetch(`${API_URL}/api/admin/users/${user.user_id}/call-limit`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ call_limit_override: newLimit }),
        });
        const d = await readJsonOnce(r);
        if (!r.ok) throw new Error((d && d.detail) || 'Failed to set call limit');
      }

      // 3. verification flag
      if (verified !== Boolean(user.email_verified)) {
        const r = await fetch(`${API_URL}/api/admin/users/${user.user_id}/verify`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email_verified: verified }),
        });
        const d = await readJsonOnce(r);
        if (!r.ok) throw new Error((d && d.detail) || 'Failed to toggle verification');
      }

      toast.success('User updated');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card
        className="max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-edit-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold">Manage user</h2>
            <p className="text-xs text-muted-foreground">{user.email}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-5">
          <div>
            <label className="text-sm font-medium block mb-1">Plan</label>
            <select
              data-testid="admin-edit-plan"
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
            >
              {Object.values(plans).map((p) => (
                <option key={p.id} value={p.id}>{p.name} {p.price > 0 ? `· $${p.price}/mo` : ''}{p.legacy ? ' (legacy)' : ''}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Billing cycle</label>
            <div className="flex gap-2">
              {['monthly', 'annual'].map((c) => (
                <Button
                  key={c}
                  type="button"
                  variant={cycle === c ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCycle(c)}
                  data-testid={`admin-edit-cycle-${c}`}
                >
                  {c === 'monthly' ? 'Monthly' : 'Annual (−20%)'}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Per-user call limit override</label>
            <Input
              data-testid="admin-edit-call-limit"
              type="number"
              min="0"
              value={callLimit}
              onChange={(e) => setCallLimit(e.target.value)}
              placeholder="Leave blank to use plan default"
            />
            <p className="text-xs text-muted-foreground mt-1">Overrides the plan's call_analyses limit for this single user.</p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium block">Email verified</label>
              <p className="text-xs text-muted-foreground">Bypass the verification email for beta access.</p>
            </div>
            <Button
              type="button"
              variant={verified ? 'default' : 'outline'}
              size="sm"
              onClick={() => setVerified(!verified)}
              data-testid="admin-edit-verify-toggle"
            >
              {verified ? 'Verified' : 'Not verified'}
            </Button>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Note (optional)</label>
            <Input
              data-testid="admin-edit-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Beta tester comp, internal team, podcast partner..."
            />
            <p className="text-xs text-muted-foreground mt-1">Stored in the plan history audit trail.</p>
          </div>

          <div className="border border-red-200 bg-red-50/60 rounded-md p-4" data-testid="admin-danger-zone">
            <div className="flex items-start justify-between gap-3">
              <div>
                <label className="text-sm font-semibold text-red-700 block">Danger zone</label>
                <p className="text-xs text-red-700/80 mt-0.5">
                  Permanently deletes this user, their calls, analyses, deals, tasks, subscription and sessions. This cannot be undone.
                </p>
              </div>
              {!confirmDelete && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-red-300 text-red-700 hover:bg-red-100 shrink-0"
                  onClick={() => setConfirmDelete(true)}
                  data-testid="admin-delete-user-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete user
                </Button>
              )}
            </div>
            {confirmDelete && (
              <div className="mt-3 space-y-2">
                <p className="text-xs text-red-800">Type <span className="font-mono font-semibold">{user.email}</span> to confirm.</p>
                <Input
                  data-testid="admin-delete-confirm-input"
                  value={deleteText}
                  onChange={(e) => setDeleteText(e.target.value)}
                  placeholder={user.email}
                  className="border-red-300"
                />
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setConfirmDelete(false); setDeleteText(''); }} disabled={deleting}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="bg-red-600 hover:bg-red-700 text-white"
                    disabled={deleting || deleteText.trim().toLowerCase() !== user.email.toLowerCase()}
                    onClick={deleteUser}
                    data-testid="admin-delete-confirm-btn"
                  >
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                    Permanently delete
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="admin-edit-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save changes
          </Button>
        </div>
      </Card>
    </div>
  );
}
